import type { Stylist } from '../types/stylist';

/**
 * Live totals for stylist profile rating UI. Prefer `stats` from
 * `GET /reviews/stylist/:id` — the same contract mobile uses in
 * `ReviewsTab` via `reviewService.getStylistReviews` (aggregates match the
 * published reviews list). Embedded stylist fields are only a fallback before
 * that response arrives or when `liveReviewSummary` is omitted.
 */
export type StylistLiveReviewSummary =
  | 'loading'
  | { count: number; average: number };

export function getEmbeddedStylistRating(stylist: Stylist): {
  average: number;
  count: number;
} {
  const s = stylist as Stylist & {
    metrics?: { ratings?: { average?: number; count?: number } };
    totalReviews?: number;
  };
  return {
    average:
      s.metrics?.ratings?.average ??
      stylist.ratings?.average ??
      stylist.rating ??
      0,
    count:
      s.metrics?.ratings?.count ??
      stylist.ratings?.count ??
      s.totalReviews ??
      stylist.reviewCount ??
      0,
  };
}

/**
 * Merges profile-embedded rating fields with a live summary from the reviews API.
 * When `live` is `'loading'`, `loading` is true and averages/counts fall back to
 * embedded values (typically not shown in the UI while loading).
 */
export function ratingFromLiveSummary(
  embedded: { average: number; count: number },
  live: StylistLiveReviewSummary | undefined
): { average: number; count: number; loading: boolean } {
  if (live === 'loading') {
    return { ...embedded, loading: true };
  }
  if (live && typeof live === 'object') {
    return { average: live.average, count: live.count, loading: false };
  }
  return { ...embedded, loading: false };
}
